import { cn } from '../lib/cn';
import { CloseIcon, VerifiedBadge, SunIcon, MoonIcon } from './icons';
import type { BrandingConfig } from '../client/api-types';
import type { ConnectionStatus } from '../client/socket';
import type { ActiveResponder, AvailabilityStatus } from './useChatState';
import { t } from '../lib/i18n';
import type { Theme } from '../lib/theme';

interface PanelHeaderProps {
  branding: BrandingConfig;
  onClose: () => void;
  connectionStatus?: ConnectionStatus;
  agentTyping?: boolean;
  verified?: boolean;
  headerStyle?: 'rich' | 'compact';
  theme?: Theme;
  onToggleTheme?: () => void;
  /**
   * Who is currently answering on the agent side. When present, the
   * subtitle shows their name (or `t('ai_agent')` for AI members when
   * `branding.anonymize_ai_agent` is true) instead of the generic
   * "responde al instante" copy.
   */
  activeResponder?: ActiveResponder | null;
  /**
   * Hide the close (X) button. The standalone page IS the chat — there's
   * nowhere to close to, so the button would be misleading. The theme toggle
   * still renders.
   */
  hideClose?: boolean;
  /** Current availability status. */
  availabilityStatus?: AvailabilityStatus;
  /** Human-readable "next available" label, e.g. "mañana 9:00". */
  nextAvailableLabel?: string | null;
}

function StatusDot({
  status,
  availabilityStatus = 'online',
}: {
  status: ConnectionStatus;
  availabilityStatus?: AvailabilityStatus;
}) {
  if (status === 'connected') {
    // Show gray dot when connected but business is offline
    if (availabilityStatus === 'offline') {
      return (
        <span
          aria-label={t('offline_away')}
          class={cn(
            'inline-block w-[13px] h-[13px] rounded-full bg-gray-400 flex-shrink-0',
            'ring-[2.5px] ring-w-hbg',
            'absolute bottom-0 right-0',
          )}
        />
      );
    }
    return (
      <span
        aria-label={t('online')}
        class={cn(
          'inline-block w-[13px] h-[13px] rounded-full bg-[#35D07F] flex-shrink-0',
          'ring-[2.5px] ring-w-hbg',
          // Position over the avatar bottom-right
          'absolute bottom-0 right-0',
        )}
      />
    );
  }
  if (status === 'connecting' || status === 'reconnecting') {
    return (
      <span
        aria-label={t('connecting')}
        class="inline-block w-[13px] h-[13px] rounded-full bg-yellow-300 animate-pulse flex-shrink-0 absolute bottom-0 right-0 ring-[2.5px] ring-w-hbg"
      />
    );
  }
  return null;
}

export function PanelHeader({
  branding,
  onClose,
  connectionStatus,
  agentTyping = false,
  verified = false,
  headerStyle = 'rich',
  theme = 'light',
  onToggleTheme,
  activeResponder = null,
  hideClose = false,
  availabilityStatus = 'online',
  nextAvailableLabel,
}: PanelHeaderProps) {
  const isRich = headerStyle === 'rich';
  // `show_online_status` scopes to the ambient connection indicator only:
  // the green dot on the avatar and the fallback "responde al instante"
  // subtitle. It does NOT gate contextual state like typing or the
  // active responder's name — those still surface when known so the
  // visitor can tell who is answering even on owners without 24/7
  // coverage. Widgets predating this flag keep the current behavior.
  const showOnlineStatus = branding.show_online_status !== false;

  function getSubtitle(): string | null {
    // Connection troubles override everything — the visitor needs to see
    // the actual state of the pipe first.
    if (connectionStatus === 'connecting') return t('connecting');
    if (connectionStatus === 'reconnecting') return t('reconnecting');
    if (connectionStatus && connectionStatus !== 'connected') {
      return t('offline');
    }
    // Typing indicator is more useful than the responder name mid-reply.
    if (agentTyping) return t('typing');
    // Who's answering — shown once the backend has told us. The display
    // depends on `agent_identity_mode` (with backward compat fallback to
    // the legacy `anonymize_ai_agent` boolean):
    //   - 'name': show the agent's real name
    //   - 'role': show a generic role label (AI → "Agente de IA", human → "Agente de soporte")
    //   - 'hidden': don't show any agent identity
    if (activeResponder) {
      // Resolve identity mode: new field takes precedence, fall back to legacy
      const identityMode: 'name' | 'role' | 'hidden' =
        branding.agent_identity_mode ??
        (branding.anonymize_ai_agent === true ? 'role' : 'name');

      if (identityMode === 'hidden') {
        // Skip agent identity — fall through to online status or null
      } else if (identityMode === 'role') {
        return activeResponder.type === 'ai' ? t('ai_agent') : t('human_agent');
      } else {
        return activeResponder.name;
      }
    }
    // Offline status — show "fuera de horario" or "regresa a las X"
    if (availabilityStatus === 'offline') {
      if (nextAvailableLabel) {
        return t('offline_back_at', { time: nextAvailableLabel });
      }
      return t('offline_away');
    }
    // The generic "responde al instante" copy is the only piece the online
    // status flag actually gates. When the owner opts out, we render no
    // subtitle at all — the header collapses to just the widget name.
    if (!showOnlineStatus) return null;
    return t('online_responds_instantly');
  }

  return (
    <div
      class={cn(
        'flex items-center gap-3 px-4 text-w-hfg transition-[background] duration-[350ms] ease-in-out',
        isRich ? 'py-3' : 'py-2.5',
      )}
      style={{ background: 'var(--w-hbg)' }}
    >
      {/* Avatar with status dot */}
      <div class="relative flex-shrink-0">
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.name}
            class={cn(
              'rounded-full object-cover',
              theme === 'light' ? 'bg-white' : 'bg-[var(--brand)]',
              isRich ? 'w-[42px] h-[42px]' : 'w-[34px] h-[34px]',
            )}
          />
        ) : (
          <div
            class={cn(
              'rounded-full flex items-center justify-center font-bold',
              theme === 'light'
                ? 'bg-white text-[var(--brand)]'
                : 'bg-[var(--brand)] text-white',
              isRich
                ? 'w-[42px] h-[42px] text-[16px]'
                : 'w-[34px] h-[34px] text-[14px]',
            )}
          >
            {branding.name.charAt(0).toUpperCase()}
          </div>
        )}
        {showOnlineStatus && connectionStatus && (
          <StatusDot
            status={connectionStatus}
            availabilityStatus={availabilityStatus}
          />
        )}
      </div>

      {/* Name + subtitle */}
      <div class="flex-1 min-w-0">
        <p
          class={cn(
            'm-0 font-semibold truncate flex items-center gap-1.5',
            isRich ? 'text-[16.5px]' : 'text-[14px]',
          )}
        >
          {branding.name}
          {verified && (
            <VerifiedBadge class="w-[14px] h-[14px] flex-shrink-0" />
          )}
        </p>
        {isRich &&
          (() => {
            const subtitle = getSubtitle();
            return subtitle ? (
              <p class="m-0 text-[12.5px] text-w-hsub truncate">{subtitle}</p>
            ) : null;
          })()}
      </div>

      {/* Action buttons */}
      <div class="flex items-center gap-1.5">
        {onToggleTheme && (
          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={theme === 'light' ? t('dark_mode') : t('light_mode')}
            class={cn(
              'flex items-center justify-center w-[34px] h-[34px] rounded-full',
              'bg-white/[.16] hover:bg-white/[.24] transition-colors duration-150',
              'border-0 text-white cursor-pointer outline-none',
              'focus-visible:ring-2 focus-visible:ring-white/50',
            )}
          >
            {theme === 'light' ? (
              <MoonIcon class="w-[15px] h-[15px]" />
            ) : (
              <SunIcon class="w-[15px] h-[15px]" />
            )}
          </button>
        )}
        {!hideClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close_chat')}
            class={cn(
              'flex items-center justify-center w-[34px] h-[34px] rounded-full',
              'bg-white/[.16] hover:bg-white/[.24] transition-colors duration-150',
              'border-0 text-white cursor-pointer outline-none',
              'focus-visible:ring-2 focus-visible:ring-white/50',
            )}
          >
            <CloseIcon class="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
